/**
 * Animation controller for one Mixamo vertical-slice player.
 *
 * Owns every AnimationAction for its skeleton. Gameplay code asks for semantic
 * things — "you are moving at 3.1 m/s", "play a PASS_SHORT" — and never touches
 * an AnimationAction, because the moment two callers can poke the same action
 * the weights stop summing to one and nobody can tell which caller left a limb
 * half-blended.
 *
 * THE CLIP VOCABULARY STOPS HERE. Callers speak `AnimCommand`; the translation
 * to a clip name lives in `CLIP_FOR_COMMAND` below and nowhere else. Gameplay
 * naming a clip is how a re-authored animation set silently stops playing (see
 * animationCommand.ts's header) — `animationWiring.test.ts` asserts mechanically
 * that no clip string escapes this file.
 *
 * Scope note: no foot IK here on purpose. This slice exists to measure the raw
 * output — whether cadence matches ground speed and how much the feet slide.
 * Adding IK now would hide the very defect we are trying to size.
 */
import {
  AnimationMixer,
  LoopOnce,
  LoopRepeat,
  type AnimationAction,
  type AnimationClip,
  type Object3D,
} from 'three';
import {
  ACTION_CONTACT_BEAT, ANIM_COMMAND_COUNT, AnimCommand, animCommandName,
  isDirectionalGait, isOneShotCommand,
} from './animationCommand';
import { legScaleFromRestHips, scaleClipsForLegs } from './hipsLegScale';

/** Locomotion states, ordered slow → fast. Order is used for band selection. */
export type LocoState =
  | 'Idle'
  // The stroll. Named for its clip like every other state here. See
  // GAIT_BANDS_WITH_STROLL for why it exists and what it replaced.
  | 'SlowWalkForward'
  | 'Walk'
  | 'Jog'
  | 'Run'
  | 'Sprint'
  | 'Backpedal'
  | 'StrafeLeft'
  | 'StrafeRight'
  | 'Dribble';

/** One-shot actions. */
export type ActionName = 'Pass' | 'Penalty' | 'ThrowIn' | 'Header';

/**
 * NO CLIP HAS BEEN AUTHORED FOR THIS COMMAND YET.
 *
 * A distinct marker rather than `undefined`, because "nobody has drawn this
 * yet" and "somebody forgot a row" must never look the same in a table. A
 * command bound to this degrades to the locomotion ladder — never a T-pose,
 * never a throw — and says so exactly once per command per session, so a
 * missing goalkeeper set is a line in the console instead of a mystery.
 *
 * Another agent is authoring the goalkeeper, tackle, fall and get-up clips in
 * parallel; each becomes a one-line change to the table below.
 */
export const NOT_YET_AVAILABLE = Symbol('NOT_YET_AVAILABLE');
export type ClipBinding = string | typeof NOT_YET_AVAILABLE;

/**
 * THE ONLY PLACE A SEMANTIC COMMAND BECOMES A CLIP.
 *
 * Exhaustive over `AnimCommand` on purpose: the compiler rejects a new command
 * that nobody has decided how to draw. The shipped GLB holds twelve clips
 * (eight gaits + Pass/Penalty/ThrowIn/Header), so most of the table is honestly
 * NOT_YET_AVAILABLE — that is a statement about the ASSET, not about the wiring,
 * and the wiring is what makes the gap visible.
 *
 * `Penalty` backs SHOOT because it is the shot clip in this set — a penalty is a
 * placed shot and it is the only full-power strike authored. VOLLEY and
 * BICYCLE_KICK are deliberately NOT aliased onto it: a scissor kick played as a
 * standing penalty is worse than no clip, because it reads as correct.
 */
const CLIP_FOR_COMMAND: Readonly<Record<AnimCommand, ClipBinding>> = {
  [AnimCommand.IDLE]: 'Idle',
  [AnimCommand.WALK]: 'Walk',
  [AnimCommand.JOG]: 'Jog',
  [AnimCommand.RUN]: 'Run',
  [AnimCommand.SPRINT]: 'Sprint',
  [AnimCommand.BACKPEDAL]: 'Backpedal',
  [AnimCommand.STRAFE_LEFT]: 'StrafeLeft',
  [AnimCommand.STRAFE_RIGHT]: 'StrafeRight',
  // ShortPassRight (0.800 s, authored on the 74-bone master) replaces M_Pass
  // (1.633 s, from the mocap library). Two measured reasons, both owner-reported:
  //   - DELAYED: the sim releases the ball 0.196 s after the button
  //     (ShortPass contactWindowStart 0.42 x 28 ticks), while M_Pass's beat
  //     put the visible boot on the ball at 0.686 s — the ball left half a
  //     second before the foot swung.
  //   - BAD: from the GAME camera M_Pass rests both hands on his backside for
  //     the first and last quarter of its 1.633 s; ShortPassRight hangs the
  //     arms naturally and swings one out for balance on the strike. That was
  //     recorded as "the highest-visibility defect found this session" and the
  //     rebind was deferred then for a timing reason this change now fixes.
  // The KICK's clip, deliberately shared (owner: "make the pass as quick as
  // the kick"). Kick is the 0.600 s in-stride swing; ShortPassRight (0.800 s)
  // read a beat slower even with its strike on the release tick. Sharing a
  // clip between commands is the table's design — SlideRecover shares GET_UP
  // the same way — and the ball's own speed and direction already tell the
  // viewer which verb it was.
  // THE KICK'S SNAP, DIFFERENT FOOT (owner, 2026-08-15: "use kick animation
  // for the pass too but with a different variation"). The pass plays the
  // MIRROR of the knock-ahead's jab - same instant feel, opposite boot, so a
  // pass and a knock-ahead read as siblings rather than clones. PassSideFoot
  // and the side-foot experiment stay shelved with their provenance.
  [AnimCommand.PASS_SHORT]: 'KickRight',
  [AnimCommand.PASS_SHORT_LEFT]: 'KickRight',
  // The touchline. Compiled from tools/anim/author/clips/coach_*.py, loaded via
  // AUTHORED_COACH_CLIP_STEMS, and requested only by castPlayerVisual.
  [AnimCommand.COACH_INSTRUCT]: 'CoachInstruct',
  [AnimCommand.COACH_APPEAL]: 'CoachAppeal',
  [AnimCommand.COACH_APPLAUD]: 'CoachApplaud',
  [AnimCommand.COACH_FRUSTRATED]: 'CoachFrustrated',
  // ⚠ WAS `Penalty`, i.e. a placed penalty stood in for every shot in the game.
  // `StandardShotRight` is a real running strike: measured 32 mm of pelvis bob
  // over the swing against the penalty's 0.0, and its boot does not go through
  // the pitch (0.0 mm against the penalty's 26.5 mm of penetration). Reviewed
  // at three frames: plant, swing, follow-through — it is a normal kick.
  // The running strike: 'Strike Foward Jog.fbx' whole cycle, contact at
  // t=0.10 of 1.0 s (the shot impulse is same-tick), follow-through and
  // stride-out riding into the live gait. StandardShotRight stays loaded
  // for a future standing-shot split.
  [AnimCommand.SHOOT]: 'StrikeForwardJog',
  [AnimCommand.THROW_IN]: 'ThrowIn',
  // The 1.90 s take cut to 0.80 s with the contact at 0.17 s (owner: a ball
  // at head height "should be hit with head", plus the standing immediacy
  // rule). The old clip WAS the whole take, so its head met the ball almost a
  // second after the sim had already applied the impulse.
  [AnimCommand.HEADER]: 'HeaderStrike',
  // ── RECOVERED. Every one of these was NOT_YET_AVAILABLE and degraded to the
  //    gait ladder; every one has had a finished clip sitting unshipped on
  //    anim/keeper-dive-and-idles since @d198535. See the review notes in
  //    AUTHORED_ACTION_CLIP_STEMS.
  //
  //    JOCKEY takes the FORWARD jockey and not one of the other three. The
  //    library ships a full four-way set, but a sustained pose holds exactly one
  //    clip on the base layer and `AnimState.Jockey` carries no direction — the
  //    sim knows which way the defender is shuffling and does not publish it.
  //    Picking a direction here from velocity would be the velocity-derived
  //    second state machine animationCommand.ts exists to prevent, so the other
  //    three stay loaded and unbound until the wire carries a direction.
  [AnimCommand.JOCKEY]: 'JockeyForward',
  [AnimCommand.FATIGUED]: 'Fatigued',
  [AnimCommand.FALL]: 'FallForward',
  [AnimCommand.STUMBLE]: 'Stumble',
  [AnimCommand.THROW_IN_HOLD]: 'ThrowInHold',
  // ── goalkeeper, from the measured pack (anim/gk-core-pack) ──
  // A SUSTAINED POSE, not a gait: held on the base layer by setSustainedPose.
  [AnimCommand.GK_READY]: 'GkReady',
  [AnimCommand.PASS_LONG]: NOT_YET_AVAILABLE,
  [AnimCommand.VOLLEY]: NOT_YET_AVAILABLE,
  // The 'Scissor Kick.fbx' reference's whole arc, brought onto the rig
  // OFFLINE: dip, leap, overhead strike at t=0.10, the fall onto the back,
  // the floor gather and the take's own rise — 1.500 s = one tick of the
  // strike state + BICYCLE_RECOVERY_S + KNOCKDOWN_GETUP_S, ending standing
  // at the origin as controls return. `ownsItsRecovery` in animationDirector
  // keeps its own Fallen tick and the knockdown GET_UP from cutting it.
  [AnimCommand.BICYCLE_KICK]: 'BicycleKick',
  [AnimCommand.TACKLE]: 'StandingTackle',
  // SLIDE ends flat and prone, and GET_UP begins flat and prone. That pairing
  // is the whole reason the sim's SlideTackle -> SlideRecover chain reads as one
  // movement rather than two clips with a snap between them.
  [AnimCommand.SLIDE]: 'SlideTackle',
  [AnimCommand.GET_UP]: 'GetUpFront',
  // SKILL stays unbound: the library has no skill-move clip, and aliasing a
  // celebration or a stumble onto it would read as correct and be wrong.
  [AnimCommand.SKILL]: NOT_YET_AVAILABLE,
  [AnimCommand.CELEBRATE]: 'Celebrate',
  [AnimCommand.JUMP]: 'Jump',
  [AnimCommand.PUNCH]: NOT_YET_AVAILABLE,
  // ⚠ WAS NOT_YET_AVAILABLE, AND THE LIGHT SHOT NEVER DREW. AnimState.ShootLight
  // (the knock-ahead: a ground shot charged under SHOT_LIGHT_CHARGE_S) has
  // mapped to KICK since 2026-08-14 (6b9196d), and its session log says
  // "ShootLight -> KICK -> the Kick clip" — but this row was never touched, so
  // every light kick degraded to the gait ladder: the ball left and the legs
  // kept jogging. Measured 2026-08-16 by the clip audit (clip-metrics META).
  // `Kick` is the 0.6 s left-footed in-stride jab that PASS_SHORT played for
  // two days; the same swing on the same command family. AnimState.Kick (the
  // house-rule violent-conduct kick) reaches this row too, and a kick clip is
  // the honest thing to draw for it.
  [AnimCommand.KICK]: 'Kick',
  // The mid dives, not the low ones: `AnimState` carries only a SIDE, and the
  // pack's own `selectDiveClip(side, ballHeightM)` needs a ball height the wire
  // does not publish. Mid is the honest single choice — a save at any height
  // reads as a save, where committing to Low would show a keeper going to
  // ground under a ball at his chest.
  [AnimCommand.GK_DIVE_LEFT]: 'GkDiveMidLeft',
  [AnimCommand.GK_DIVE_RIGHT]: 'GkDiveMidRight',
  [AnimCommand.GK_CATCH]: 'GkCatchStanding',
  [AnimCommand.GK_PICKUP_GROUND]: 'GkPickupGround',
  [AnimCommand.GK_PLACE_BALL]: 'GkPlaceBall',
  [AnimCommand.CARRY_TURN]: 'CarryTurn',
  // GK_PUNCH stays unbound ON PURPOSE and is classified INTENTIONALLY_UNSERVED,
  // not NOT_YET_AVAILABLE — the pack shipped `GK_ANIM_CLIP.GkPunch = null` with
  // a test pinning that exact list. A keeper punching a cross clear is a
  // two-fisted upward strike; the nearest thing in the library is the outfield
  // violent-conduct punch, and aliasing them is the "visually unrelated" case.
  [AnimCommand.GK_PUNCH]: NOT_YET_AVAILABLE,
  [AnimCommand.GK_SMOTHER]: 'GkSmother',
  [AnimCommand.GK_THROW]: 'GkOverarmThrow',
  [AnimCommand.GK_KICK]: 'GkPuntRight',
  [AnimCommand.GK_ROLL_OUT]: 'GkRollout',
  // The control touches, from the Mixamo control takes. Each is the whole
  // gesture — knee up and the ball down to the boot; chest cushion and the
  // ball down the body — starting and ending in the stance the gait blends
  // out of and back into, so a touch taken at pace flows.
  [AnimCommand.CONTROL_KNEE]: 'ControlKnee',
  [AnimCommand.CONTROL_CHEST]: 'ControlChest',
};

/** Read-only view for the wiring tests and the debug overlay. */
export function clipBindingFor(cmd: AnimCommand): ClipBinding {
  return CLIP_FOR_COMMAND[cmd];
}

/**
 * The gait ladder's own commands — the only sustained commands whose clip the
 * FORWARD/DIRECTIONAL locomotion machinery owns. Everything else in the
 * sustained half of the vocabulary is a POSE and holds the base layer itself.
 */
const GAIT_COMMANDS: readonly AnimCommand[] = [
  AnimCommand.IDLE, AnimCommand.WALK, AnimCommand.JOG, AnimCommand.RUN,
  AnimCommand.SPRINT, AnimCommand.BACKPEDAL,
  AnimCommand.STRAFE_LEFT, AnimCommand.STRAFE_RIGHT,
];

/**
 * The clip a sustained NON-GAIT command holds on the base layer, or null.
 *
 * Returns null for a gait (locomotion owns those) and null for a command with
 * no clip authored — which keeps the honest fallback intact: an undrawn jockey
 * still shows the legs moving and still logs once, because there is nothing
 * else to show. It stops being honest only when a clip DOES exist and a jog is
 * played over it, which is what this function exists to prevent.
 */
function sustainedPoseClip(cmd: AnimCommand): string | null {
  if (isOneShotCommand(cmd) || GAIT_COMMANDS.includes(cmd)) return null;
  const binding = CLIP_FOR_COMMAND[cmd];
  return typeof binding === 'string' ? binding : null;
}

/**
 * The one-shot CLIPS, derived from the table above rather than restated.
 *
 * The locomotion suppression and the residual sweep both need this set, and
 * they used to hold two separate literals — which is how a clip can end up
 * suppressed but never swept (or swept but never suppressed). Deriving it means
 * binding a new action clip cannot desynchronise them.
 */
const ONE_SHOTS: readonly string[] = (() => {
  const out: string[] = [];
  for (let i = 0; i < ANIM_COMMAND_COUNT; i++) {
    const c = i as AnimCommand;
    if (!isOneShotCommand(c)) continue;
    const clip = CLIP_FOR_COMMAND[c];
    if (typeof clip === 'string' && !out.includes(clip)) out.push(clip);
  }
  return out;
})();

/** Whether a command can actually be drawn, and if not, why not. */
export const enum ActionAvailability {
  /** A clip is bound AND present in the loaded set. */
  Available = 0,
  /** Bound to NOT_YET_AVAILABLE — a known, logged authoring gap. */
  NotYetAvailable = 1,
  /** Bound to a clip name the loaded GLB does not contain. */
  ClipMissing = 2,
  /** A sustained state; it drives the base layer and is never "played". */
  NotAnAction = 3,
}

/**
 * Natural ground speed of each travelling clip, measured in Blender from root
 * displacement over duration (see reports/locomotion-analysis.json). Playback
 * rate is derived from these, so a wrong number here shows up directly as foot
 * sliding.
 */
export const NATURAL_SPEED: Readonly<Record<string, number>> = {
  // MEASURED 2026-08-10 with tools/anim/measure/slip-fit.mjs: cycle 1.184 m over
  // 1.1935 s, residual slip 31.1 mm — the CLEANEST fit of the whole locomotion
  // set — with a 50/52 % stance duty, which is a textbook walk. The median
  // walking speed in live telemetry is 0.98 m/s, so this clip's natural speed
  // and the speed the game actually walks at agree to within 1 %.
  SlowWalkForward: 0.99,
  // ── RE-DERIVED 2026-08-10, AFTER THE DEAD-FRAME RETIME ─────────────────────
  //
  // `M_Walk`, `M_Jog` and `M_Run` each keyed their first sample at t = 1/30
  // while the clip's duration was the LAST key time, so glTF clamped the first
  // 1/30 s of every loop into a frozen pose with the ground moving underneath.
  // That hold was the whole of each clip's "residual slip" AND it depressed
  // every fitted cadence by K/(K-1) — so the three numbers below were fitted
  // against a stopped clock. All three keys now span 0..duration
  // (tools/anim/measure/retime-clip.mjs); no pose moved.
  //
  // Measured on THE CLIPS THAT SHIP, on three instruments that share no code:
  //
  //   gait  FK treadmill fit   production chord   slide-minimising sweep  ADOPTED
  //   Walk  1.798              1.805              1.83                    1.80
  //   Jog   2.532              2.462              2.56                    2.53
  //   Run   3.022              3.124              3.24                    3.12
  //
  // (FK = tools/anim/measure/slip-fit.mjs, dependency-free. Chord =
  // footSlide.ts `measuredNaturalSpeed` through the production loader. Sweep =
  // the ground speed at which that same instrument measures the least slide.)
  //
  // Walk and Jog: the three agree inside 3 %, so the treadmill fit — the
  // instrument that set Sprint's 6.01 — is taken as it stands.
  //
  // ⚠ Run: they span 7 %, and that is a FINDING rather than noise. `M_Run` is
  // authored by blending `M_Jog` and `M_Sprint` (build-run.mjs), and both donors
  // were sampled THROUGH their own dead frame, so a slice of the run's cycle is
  // phase-compressed. Its stance therefore sweeps at two different rates and no
  // single cadence describes it: the residual slip only falls 112 -> 88 mm on
  // the retime where walk and jog collapse to 35 and 26. The chord is taken for
  // Run because it is the only one of the three that averages the WHOLE stance
  // instead of landing on one end of it. Closing it properly means re-blending
  // the run from retimed donors, which is a re-author and needs its own verdict.
  // ⚠ RE-MEASURED 2026-08-13. M_Walk, M_Jog, M_Run and M_Sprint were all
  // REBUILT OFFLINE from the RealMotion take library onto the frozen rig (see
  // tools/anim/ and the .provenance.json beside each clip), so every number
  // below describes a DIFFERENT clip from the one it described yesterday.
  // They are read back off the shipped GLBs by shippedGaitCadence.test.ts,
  // which is what caught the stale table: it measured Run at 4.166 m/s against
  // a declared 3.12 and Sprint at 7.336 against 6.01, and a divisor that wrong
  // is foot-skate you can see.
  //
  // Each value is the FIXED POINT of that measurement, not the measurement
  // itself: the reported figure moves inversely with the table (the animator
  // plays the clip at a rate derived from it), so table = sqrt(table x
  // measured). Setting the raw reading instead just overshoots the other way -
  // Walk read 1.586 at a table of 2.0 and 2.065 at a table of 1.586.
  // ⚠ THESE ARE NOT THE TREADMILL FIT, AND THAT IS DELIBERATE.
  // slip-fit.mjs fits each clip alone and returns Walk 2.076, Jog 2.370,
  // Run 4.344, Sprint 7.036 - the speeds at which each clip's stance sweeps
  // with least slip (18.5 / 27.4 / 36.7 / 11.7 mm). Setting them made the game
  // WORSE, not better: this table also positions the locomotion band edges
  // (they are geometric means of adjacent entries), so Walk at 2.076 pushed the
  // Walk/Jog edge past 1.8 and a player walking at 1.5 m/s selected Jog. Walk's
  // measured skate did not improve either - 0.163 against 0.161 here.
  //
  // So each value is the fixed point of the animator's own cadence reading,
  // which keeps the band edges where the sim expects them. The fitter's numbers
  // are recorded above because they are the right target for a future retime:
  // the gap between the two IS the remaining skate.
  // ⚠ EACH VALUE IS THE CENTRE OF THE RANGE THAT STATE ACTUALLY SERVES, AND
  // THE CLIP WAS MOVED IN TIME TO MEET IT.
  //
  // The clips arrived far too FAST for the speeds they serve. Run covers
  // [2.50, 4.30] m/s and its clip swept at 3.956, so the animator played it at
  // rate 0.63-0.77 across most of that - and a stance played slower than it was
  // captured sweeps FASTER than the ground beneath it. Measured before the fix:
  // at 3.06 m/s the run skated 22.4 cm per stride. That is the sliding you see
  // on the pitch. Walk, Jog and Sprint were the same shape of wrong.
  //
  // Each clip was time-scaled (tools/anim/measure/scale-clip-time.mjs) until its
  // fitted sweep equals the value below, so the rate is now ~1.00 mid-range
  // instead of ~0.7. No pose was touched: only the shared key-time accessor.
  //
  //   state   serves          centre   clip was   slide frac (was)
  //   Walk    [1.20, 2.00]     1.549     1.780      0.06  (0.237)
  //   Jog     [1.70, 2.85]     2.201     2.530      0.04  (0.183)
  //   Run     [2.50, 4.30]     3.279     3.956      0.10  (—)
  //   Sprint  [3.95, 8.50]     5.794     7.100      0.07  (0.080)
  //
  // ⚠ THE RANGE IS THIS STATE'S `down` TO THE NEXT STATE'S `up`, not its own
  // `up`: those are hysteresis thresholds for stepping between states. Reading
  // Run's range as [2.50, 2.85] put its centre at 2.669, and since a clip may
  // serve anything inside the [0.65, 1.45] rate clamp, that let Walk reach
  // 1.84 m/s and steal Jog's band - the selector returned Walk where Jog
  // belonged. Sprint's top edge is MAX_SPEED_SPRINT; it is the open-ended band.
  // ⚠ RE-MEASURED 2026-08-14 AGAINST A DIFFERENT CLIP. `M_Walk` is gone; `Walk`
  // is keyed by hand in tools/anim/author/clips/walk.py and it is a genuinely
  // slower gait — 36 frames over 1.2 s where the retarget did 35 over 1.161,
  // and a shorter stride inside that. shippedGaitCadence read 1.007 m/s against
  // the old table of 1.549, so the fixed point is sqrt(1.549 x 1.007) = 1.249.
  // The table follows the asset, always: a stale number here is foot slide.
  Walk: 1.121,
  // RE-MEASURED 2026-08-13 after the jog's thighs were abducted to stop its two
  // boots overlapping from behind (92.3 -> 118.8 mm minimum separation). Opening
  // the track shortens the ground each stride covers, so the clip carries LESS
  // speed per cycle, not more: shippedGaitCadence measured 2.143 against 2.201.
  // The divisor follows the asset — a stale number here is foot slide, and this
  // one is the opposite sign to the Run's, which the same pass made faster.
  Jog: 2.143,
  // RE-MEASURED 2026-08-13 after the leg-separation pass (the thighs were
  // abducted so the ankles track under their own hips instead of a single
  // centreline — see leg-track.mjs). Widening the track lengthened the foot's
  // ground sweep, so the clip genuinely carries more speed per cycle:
  // shippedGaitCadence measured 3.421 against the old 3.279 (+4.3%). Walk, Jog
  // and Sprint were re-measured by the same instrument and did NOT move. The
  // table follows the asset, always — a stale number here is foot slide.
  // ⚠ RE-MEASURED 2026-08-14 AGAINST A DIFFERENT CLIP, same as Walk above.
  // `Run` is authored (tools/anim/author/clips/run.py) at a declared 3.18 m/s —
  // duty 0.32, two 0.335 m stances over a 0.667 s cycle. Driven through the
  // real animator it reads 2.4, so the fixed point is sqrt(3.42 x 2.4) = 2.865.
  // The clip's own arithmetic and the instrument disagree because the stance
  // reach is CLAMPED by leg length (see run.py's REACH note) — the ankle target
  // is pulled in and the ground the foot actually sweeps is shorter than the
  // number the module was written to.
  Run: 3.1,
  // RE-MEASURED 2026-08-10 after M_Sprint was re-authored as a steady-state
  // top-speed sprint (tools/anim/measure/build-sprint.mjs): cycle 3.408 m over
  // 0.5667 s, residual slip 39.6 mm, stance duty 32/32 %. Step 1.704 m.
  //
  // It moved for TWO reasons and both are in the clip, not in this table:
  //   * the clip's 17 keys used to start at t = 1/30 while its duration was the
  //     LAST key time, so the first 1/30 s of every loop was a frozen pose. That
  //     hold was the ENTIRE residual slip of every gait in this pack (it is
  //     exactly v*/30 on all four) and it also depressed the fitted cadence by
  //     17/16. Sprint's keys now span 0..duration; the other three still do not.
  //   * the stride was genuinely short for a top-speed clip and was lengthened.
  //
  // At 6.01 the clip serves 3.91–8.71 m/s inside the [0.65, 1.45] rate clamp,
  // and a live match asks it for 3.94–8.50 (p1–p99). It is the only gait in the
  // set that one clip now covers: the 41.8 % of sprint frames that used to pin
  // at the rate ceiling is 0.0 % (scripts/qa-rate-clamp.mjs). At the 8.5 m/s
  // MAX_SPEED_SPRINT the rate is 1.414 and the cadence 4.99 steps/s, which is
  // elite but human — the point of the whole exercise.
  Sprint: 5.794,
  Backpedal: 3.03,
  // MEASURED off the shipped clips' own baked travel (see rootMotion.ts and
  // animationPipeline.test.ts), not carried over from the forward set. Both
  // strafes read 1.82 here for a long time, which was a guess: StrafeLeft
  // actually covers 1.888 m in 0.700 s and StrafeRight 1.576 m in 0.733 s. The
  // 48% error on the left made every playback rate 48% too fast and the feet
  // absorbed all of it — the strafes had by far the worst slide in the set.
  // They are NOT equal to each other, and asserting they were is what hid it.
  StrafeLeft: 2.7,
  StrafeRight: 2.15,
  // MEASURED 2026-08-23 (tools/anim/measure/slip-fit.mjs, same treadmill fit
  // as the entries above): v* 2.372 m/s, residual slip 28.2 mm — a clean fit,
  // in the same class as SlowWalkForward's 31.1 mm rather than a noisy one.
  // Owner, 2026-08-23: "the dribble where the ball is move left foot to
  // right" (wiring the clip into `selectLocoState` below). Its honest range
  // under the [0.65, 1.45] rate clamp is 1.54-3.44 m/s, which brackets
  // DRIBBLE_WEAVE_MIN_SPEED (2.6, sim/constants.ts) — the speed above which
  // the ball itself starts alternating feet — so the clip's natural cadence
  // and the sim's own foot-to-foot ball weave land in the same speed
  // neighbourhood without either being tuned to match the other.
  Dribble: 2.372,
};

/**
 * Forward gait bands with hysteresis. `up` is the speed at which we step into
 * the next gait, `down` the speed at which we fall back out of it. They are
 * deliberately not equal: with a single threshold, a player holding station
 * right on the boundary flickers between two gaits every frame.
 */
type GaitBand = { state: LocoState; up: number; down: number };

/**
 * THE LADDER WITHOUT A STROLL — what shipped until 2026-08-10, kept as the
 * fallback for any clip set that does not carry `SlowWalkForward`.
 *
 * ⚠ Walk's band opens at 0.35 m/s here, and that is the defect below. Do not
 * "simplify" by deleting the other table and keeping this one.
 */
const GAIT_BANDS_NO_STROLL: ReadonlyArray<GaitBand> = [
  { state: 'Idle', up: 0.0, down: 0.0 },
  { state: 'Walk', up: 0.35, down: 0.2 },
  // Same 2026-08-14 re-derivation as the stroll table below — Walk and Run are
  // different clips and their divisors moved. Only the interior edges follow;
  // Walk's `up` stays at the floor of the ladder because with no stroll clip
  // there is nothing below it to take the geometric mean against.
  { state: 'Jog', up: 1.55, down: 1.4 },
  { state: 'Run', up: 2.578, down: 2.23 },
  // Sprint's `up` is the same geometric-mean rule the stroll edge uses — and it
  // MOVED 2026-08-14 with Run's divisor: sqrt(2.865 x 5.794) = 4.074, down from
  // the 4.29 that sqrt(3.06 x 6.01) gave. `down` is pinned to Sprint's own
  // honest floor, 0.65 x 5.794 = 3.766, because a band whose bottom sits below
  // its own clip's floor is asking the rate clamp to cover for it; that leaves
  // a 0.27 hysteresis gap rather than Run's 0.35, which is the right way to
  // lose the argument between the two rules.
  { state: 'Sprint', up: 4.238, down: 3.89 },
];

/**
 * THE LADDER WITH THE STROLL BOUND. Preferred whenever the clip is loaded.
 *
 * WHY. `M_Walk`'s stride is 1.882 m per cycle, so at playback rate 1.0 it
 * carries 1.764 m/s and the rate floor of 0.65 puts its slowest honest speed at
 * 1.04 m/s. Walk's band opened at 0.35. Live telemetry (`qa-rate-clamp.mjs`)
 * measured the consequence: **53.1 % of all walking frames were below the rate
 * floor** — over half of the walking in the game was a moon-walk, with the
 * median walk sitting at 0.98 m/s against a clip that could not go below 1.04.
 *
 * No rate fixes that, and this is the part worth understanding before touching
 * these numbers. A human walking slower does not play the same stride in slow
 * motion; he takes SHORTER STEPS. Covering 0.35 m/s with `M_Walk`'s 0.94 m step
 * would need rate 0.198, i.e. a 5.4-second stride — nothing that happens at any
 * speed. `animationPipeline.test.ts` already said so: both ends of the ladder
 * "close by adding a clip, not by tuning a number". This is that clip.
 *
 * `SlowWalkForward` steps 0.59 m at 0.99 m/s and was sitting loaded but unbound
 * in `AUTHORED_UNBOUND_CLIP_STEMS` the whole time.
 *
 * The Walk/stroll edge is the GEOMETRIC MEAN of the two clips' natural speeds
 * (√(0.99 × 1.6) = 1.26), i.e. the speed at which each is equally wrong, so
 * neither clip is asked to stretch further than the other.
 */
const GAIT_BANDS_WITH_STROLL: ReadonlyArray<GaitBand> = [
  { state: 'Idle', up: 0.0, down: 0.0 },
  { state: 'SlowWalkForward', up: 0.35, down: 0.2 },
  // ⚠ MOVED 2026-08-10 with `NATURAL_SPEED.Walk` 1.6 -> 1.80. This edge is the
  // GEOMETRIC MEAN of the two clips' natural speeds — the speed at which each is
  // equally wrong — so it follows the divisor: sqrt(0.99 x 1.80) = 1.335.
  //
  // `down` had to move too, and that is the part with teeth. `M_Walk`'s honest
  // floor is 0.65 x 1.80 = 1.17 m/s, and the old `down` of 1.05 now sits BELOW
  // it — a band whose bottom is under its own clip's floor is asking the rate
  // clamp to cover for it, which is exactly what the sprint's 3.8 -> 3.95 fixed.
  // The stroll's ceiling is 1.45 x 0.99 = 1.44, so [1.20, 1.34] is inside both
  // clips' honest range and the hysteresis gap stays real.
  // ⚠ EVERY EDGE BELOW MOVED 2026-08-14, and none of them by taste. `Walk` and
  // `Run` are different clips now (authored, not retargeted) and their divisors
  // went 1.549 -> 1.249 and 3.42 -> 2.865. The two rules that set these numbers
  // are unchanged and both are applied here:
  //
  //   up   = the GEOMETRIC MEAN of the two adjacent clips' natural speeds, the
  //          speed at which each is equally wrong
  //   down = a hysteresis gap below its own `up`, and NEVER below its own
  //          clip's honest floor of 0.65 x natural
  //
  // The second rule bites twice. Jog's gap would have put `down` at 1.336
  // against a floor of 0.65 x 2.143 = 1.393, and Sprint's at 3.724 against
  // 0.65 x 5.794 = 3.766 — both bands asking the rate clamp to cover for them,
  // which is the defect the 3.8 -> 3.95 move fixed the last time. Both are
  // pinned to the floor with a smaller gap instead.
  //
  //   sqrt(0.99  x 1.249) = 1.112     Walk floor  0.65 x 1.249 = 0.81
  //   sqrt(1.249 x 2.143) = 1.636     Jog  floor  0.65 x 2.143 = 1.39
  //   sqrt(2.143 x 2.865) = 2.478     Run  floor  0.65 x 2.865 = 1.86
  //   sqrt(2.865 x 5.794) = 4.074     Sprint floor 0.65 x 5.794 = 3.77
  //
  // And every band's TOP stays inside the clip below it: Jog's up 1.636 against
  // Walk's ceiling 1.45 x 1.249 = 1.81, Sprint's up 4.074 against Run's
  // 1.45 x 2.865 = 4.15. The last of those has 78 mm/s to spare, which is the
  // tightest this ladder has ever been and the first thing to check if Run's
  // divisor moves again.
  { state: 'Walk', up: 1.053, down: 0.91 },
  { state: 'Jog', up: 1.55, down: 1.4 },
  { state: 'Run', up: 2.578, down: 2.23 },
  // Sprint's `up` is the same geometric-mean rule the stroll edge uses — and it
  // MOVED 2026-08-14 with Run's divisor: sqrt(2.865 x 5.794) = 4.074, down from
  // the 4.29 that sqrt(3.06 x 6.01) gave. `down` is pinned to Sprint's own
  // honest floor, 0.65 x 5.794 = 3.766, because a band whose bottom sits below
  // its own clip's floor is asking the rate clamp to cover for it; that leaves
  // a 0.27 hysteresis gap rather than Run's 0.35, which is the right way to
  // lose the argument between the two rules.
  { state: 'Sprint', up: 4.238, down: 3.89 },
];

/**
 * Playback rate is clamped so a clip never reads as a different gait. Below
 * ~0.65 a run turns into a moon-walk; above ~1.45 a walk acquires sprint
 * cadence. Anything outside the band means the wrong clip was selected, which
 * is a state-machine bug rather than something to stretch the rate over.
 *
 * ⚠ DO NOT WIDEN `RATE_MAX` TO COVER SPRINT — and note that the sprint no
 * longer needs it, so the temptation is gone rather than merely resisted.
 *
 * The old sprint clip pinned 36–42 % of its frames at this ceiling. Widening
 * the clamp was the obvious response and it was wrong: that clip stepped
 * 1.38 m, so at 1.45 its cadence was ALREADY 5.12 steps/s, at or past elite
 * (~4.5–5.0), and covering `MAX_SPEED_SPRINT` (8.5 m/s) would have needed rate
 * 1.744 and 6.15 steps/s, which no human does. The clamp was not what was
 * short — the STRIDE was.
 *
 * `M_Sprint` was re-authored on 2026-08-10 to the 1.70 m step that reading
 * implies (tools/anim/measure/build-sprint.mjs) and `NATURAL_SPEED.Sprint`
 * moved with it to 6.01. Measured on the same telemetry that found the 41.8 %:
 * frames above this ceiling are now **0.0 %**, and Sprint is the only gait in
 * the set that one clip covers across everything a live match asks of it.
 *
 * The lesson generalises and is why this note stays: a gait pinned at the clamp
 * is clip-limited, not rate-limited. The strafes carry the same finding and
 * they close the same way — with a different clip, never with a wider band.
 */
/**
 * Put `to` at the same point in the stride as `from`.
 *
 * The gait pack is authored so every clip STARTS on right-foot strike (within
 * 0.045 of a cycle — tools/anim/measure/roll-clip.mjs). That is what makes one
 * gait swappable for another without a blend, and it is a statement about
 * PHASE, not about time: the clips have different durations (Walk 1.161 s,
 * Jog 1.010 s, Run 0.972 s, Sprint 0.688 s), so the same phase is a different
 * number of seconds in each.
 *
 * Nothing was mapping between them. An incoming gait resumed at whatever
 * playhead it happened to be left at when that gait was last used, which is
 * unrelated to where the outgoing one is now — so the "phase-aligned, therefore
 * safe" swap was routinely putting mid-stride on top of foot-strike.
 *
 * Normalised phase is carried across, which is the mapping the alignment
 * actually licenses.
 */
function syncPhase(from: AnimationAction, to: AnimationAction): void {
  const fd = from.getClip().duration;
  const td = to.getClip().duration;
  if (fd <= 0 || td <= 0) return;
  // ⚠ FORWARD LADDER ONLY, AND THIS IS THE WHOLE OF IT. Carrying phase makes
  // sense between clips that are THE SAME MOTION AT DIFFERENT SPEEDS — a walk
  // and a run are one gait sampled twice, so "where in the stride" means the
  // same thing in both. A strafe is not a fast walk and a backpedal is not a
  // slow one; they are different motions that merely also happen to have feet.
  //
  // Measured, direction tape (`dirs`, wall-to-wall strafes and backpedals):
  // carrying phase across those transitions took snaps over 0.30 m from 23 to
  // 40, while the two forward-ladder tapes improved on every metric. Aligning
  // strike between two unrelated motions is not a correction, it is a second
  // arbitrary choice on top of the first.
  const fromName = from.getClip().name;
  const toName = to.getClip().name;
  if (!FORWARD_LADDER.has(fromName) || !FORWARD_LADDER.has(toName)) return;
  const fs = STRIKE_PHASE[fromName];
  const ts = STRIKE_PHASE[toName];
  if (fs === undefined || ts === undefined) return; // not a cyclic gait — leave it alone
  // ⚠ NEVER MOVE A CLIP THAT IS ALREADY ON THE BODY. Re-timing an action that
  // still carries weight teleports its contribution to the pose — the exact pop
  // this function exists to remove, introduced by the fix for it. Rapid ladder
  // changes leave the incoming gait part-way through a fade often enough that
  // syncing unconditionally made the direction tape measurably worse.
  if (to.enabled || to.isRunning()) return;
  // Phase measured FROM the strike, carried across, then put back into the
  // incoming clip's own frame.
  const since = (((from.time % fd) / fd) - fs + 1) % 1;
  to.time = (((since + ts) % 1)) * td;
}

/**
 * Where right-foot strike actually sits in each gait, as a fraction of its
 * cycle. Measured off the shipped GLBs (lowest `foot_r` over 180 samples):
 *
 *     SlowWalkForward 0.011   Walk 0.111   Jog 0.156   Run 0.028
 *     Sprint 0.067   Backpedal 0.017   StrafeLeft 0.050
 *     StrafeRight 0.050   Dribble 0.000
 *
 * ⚠ THE CODE ABOVE USED TO ASSUME THESE WERE ALL ZERO. `roll-clip.mjs` was
 * written to make that true and the comments in this file state it as fact —
 * "every one starts on right-foot strike, within 0.045 of a cycle". Two clips
 * never got it: StrafeRight was 0.572 and Dribble 0.728, more than half a
 * stride out. Both have since been ROLLED onto the convention (2026-08-13), so
 * the claim is finally true of the files as well as of the comments:
 * Dribble is exactly 0.000 and StrafeRight 0.050 — one key, which is the
 * resolution floor of a 22-key clip and the same figure StrafeLeft carries.
 *
 * Rolling StrafeRight needed its loop CLOSED first: it was 5.585 deg open on
 * `toe_l`, with 17 rotations past the 0.5 deg bar, so it ticked once per cycle
 * and `roll-clip.mjs` rightly refused it. `close-loop.mjs` eased it home; the
 * seam is now 0.038 deg.
 *
 * The table stays because the numbers are still not all zero and never will be
 * exactly — a clip can only express phase to one key.
 *
 * Re-measure after ANY gait re-author — the numbers are a property of the files
 * on disk, not of the rig:
 *     node scripts/anim-strike-phase.mjs
 *
 * The honest long-term fix is to roll StrafeRight and Dribble so their strike
 * lands at 0 like the rest, and then every entry here is 0 and this table can
 * go. Until then it is what makes the swap tell the truth.
 */
/**
 * The one motion sampled at five speeds. Only within this set does "the same
 * point in the stride" mean anything, so only within it is phase carried.
 */
const FORWARD_LADDER = new Set(['SlowWalkForward', 'Walk', 'Jog', 'Run', 'Sprint']);

const STRIKE_PHASE: Record<string, number> = {
  Walk: 1.121,
  Jog: 0.156,
  Run: 3.1,
  Sprint: 0.067,
  Backpedal: 0.017,
  StrafeLeft: 0.050,
  StrafeRight: 0.050,
  Dribble: 0.000,
  SlowWalkForward: 0.011,
};

const RATE_MIN = 0.65;
const RATE_MAX = 1.45;

/**
 * `Dribble`'s honest speed window — outside it the clip would have to play
 * faster/slower than RATE_MIN/RATE_MAX to match ground speed. See
 * `selectLocoState` and the `NATURAL_SPEED.Dribble` comment for how these
 * numbers were measured.
 */
const DRIBBLE_SPEED_MIN = NATURAL_SPEED.Dribble! * RATE_MIN;
const DRIBBLE_SPEED_MAX = NATURAL_SPEED.Dribble! * RATE_MAX;

/** Crossfades, seconds. Faster gaits sit closer together so they blend quicker. */
const FADE: Readonly<Record<string, number>> = {
  'Idle>SlowWalkForward': 0.22,
  'SlowWalkForward>Idle': 0.22,
  'SlowWalkForward>Walk': 0.20,
  'Walk>SlowWalkForward': 0.20,
  'Idle>Walk': 0.22,
  'Walk>Idle': 0.22,
  'Walk>Jog': 0.18,
  'Jog>Walk': 0.18,
  'Jog>Run': 0.15,
  'Run>Jog': 0.15,
  'Run>Sprint': 0.12,
  'Sprint>Run': 0.12,
  default: 0.16,
};

/** Below this an action counts as fully faded and is stopped outright. */
const RESIDUAL_EPSILON = 0.001;

/**
 * Hand-back ramp at the end of a one-shot, seconds. The action fades out over it
 * and the gait fades back in over the same window, so the two always sum to ~1.
 */
const ACTION_FADE = 0.15;

/**
 * Take-over ramp at the START of a one-shot, seconds — the mirror of
 * ACTION_FADE, and it was missing.
 *
 * `playAction` faded the action in from 0 over this window while
 * `suppressLocomotion` zeroed the gait on the same frame, so for the first
 * ~100 ms of every pass, shot, header and throw-in the weights summed to 0.17,
 * 0.33, 0.5 ... and three lerps every shortfall below 1 toward the BIND POSE.
 * The player therefore snapped a sixth of the way to a T-pose at the instant he
 * struck the ball, recovered over six frames, and did it again on the next
 * action. It is the identical defect the hand-back already documents, at the
 * other end of the clip, and it is far more visible there because it lands
 * exactly on the moment the viewer is watching.
 */
const ACTION_IN_FADE = 0.1;

/**
 * Cross-fade for a SUSTAINED POSE taking or releasing the base layer, seconds.
 *
 * Longer than a one-shot's take-over (0.1 s) because the two are different
 * events. An action is a strike and reads as sharp; dropping into a jockey
 * stance or a keeper's set position is a posture change, and 0.1 s of it looks
 * like a flinch. It matches the gait-to-gait default in `FADE` for that reason —
 * it IS a base-layer transition, just to a clip that is not a gait.
 */
const POSE_FADE = 0.2;

export interface AnimatorOptions {
  /** Clip name → clip, from the GLB. */
  clips: Map<string, AnimationClip>;
  root: Object3D;
  /**
   * This character's rest hip height above his own soles, metres — the one
   * number that turns a clip authored on the master's legs into a clip worn by
   * his. Omit it and he is animated at the master's proportions, which is what
   * every caller did before hipsLegScale.ts existed. See that file for why the
   * correction is a multiply on the hips' own vertical channel and not a shift
   * of the whole character.
   */
  restHipsY?: number | null;
}

/**
 * Read-only view of one AnimationAction, for tests and the debug overlay.
 *
 * Nothing here mutates: it exists so a caller can PROVE what the blend is doing
 * without being handed the action itself. `weights()` deliberately hides
 * everything below 0.0001, which is exactly the range a suppression has to be
 * checked in, so it cannot answer these questions.
 */
export interface ActionSnapshot {
  /** Weight the mixer will actually blend with, after any fade. */
  weight: number;
  /** Playhead inside the clip, seconds. Non-zero after a suppression proves the phase survived. */
  time: number;
  enabled: boolean;
  /** Still scheduled in the mixer. A stopped action is not. */
  running: boolean;
}

/**
 * An explicit, hand-stepped weight ramp between two actions.
 *
 * WHY NOT three's OWN FADES. `fadeOut` is unusable for the outgoing side: when a
 * fade reaches zero three sets `enabled = false`, and a disabled action stops
 * advancing its playhead — so a gait handed to `fadeOut` freezes mid-stride and
 * the phase guarantee is gone. `fadeIn`/`fadeOut` are also two INDEPENDENT
 * schedules: they only sum to 1 if both start from the weights you assumed, and
 * an interruption never does. Stepping one ramp by hand means the sum is
 * arithmetic, not a coincidence — see `stepRamp`.
 */
interface Ramp {
  /** Gains weight, up to 1. */
  to: AnimationAction | null;
  toStart: number;
  /**
   * EVERYTHING that held weight when the ramp began, scaled down together.
   *
   * A list and not a single action, because at the moment a second action is
   * requested the body can genuinely be split three ways — an outgoing gait, a
   * pass 40% faded in, and now a shot. Ramping only the "main" one down leaves
   * the rest of the weight stranded and the sum collapses; scaling the whole
   * outgoing set by (1−t) keeps it exact no matter how many parts there are.
   */
  from: AnimationAction[];
  fromStart: number[];
  /** Outgoing entries that are one-shots — torn down at the end, not just zeroed. */
  tearDown: AnimationAction[];
  elapsed: number;
  dur: number;
}

/**
 * Commands already reported as undrawable, module-wide.
 *
 * Module-level and not per-instance on purpose: twenty-two players share one
 * missing goalkeeper clip, and twenty-two identical lines is how a real message
 * gets scrolled past. One line, once per session, per command.
 */
const unavailableLogged = new Set<AnimCommand>();

/** Test seam — forget what has already been reported. */
export function resetUnavailableLog(): void {
  unavailableLogged.clear();
}

export class PlayerAnimator {
  readonly mixer: AnimationMixer;

  private readonly actions = new Map<string, AnimationAction>();
  private loco: LocoState = 'Idle';
  /**
   * When the gait last changed, in seconds on the animator's own clock. See
   * MIN_GAIT_DWELL_S and the guard in `setLocomotion`.
   */
  private locoChangedAt = -1e9;
  /** The gait we came FROM, so the dwell uses the fade that actually ran. */
  private locoPrev: LocoState = 'Idle';
  /**
   * Seconds since this animator started, advanced in `update`. The mixer has its
   * own time but it is not readable per-action, and `performance.now()` would
   * make the dwell wall-clock rather than sim time — a paused game would let it
   * expire while nothing moved.
   */
  private clock = 0;
  /**
   * A SUSTAINED POSE holding the base layer instead of the gait ladder, or null.
   *
   * The layer that was missing. `setLocomotionCommand` used to send every
   * sustained command that was not a directional gait to `setLocomotion(speed,
   * false, 0)` — i.e. to the FORWARD GAIT LADDER. That is correct for IDLE and
   * the gaits and wrong for everything else in the sustained half of the
   * vocabulary: a jockeying defender, a fallen player, a keeper in his set
   * stance and a thrower holding the ball over his head are not "running
   * forwards at their current speed". While every one of those was
   * NOT_YET_AVAILABLE the fallback was honest — there was no clip to play. The
   * moment a real clip is bound to one, playing a jog instead is the deceptive
   * kind of wrong §13 names.
   *
   * It is NOT a one-shot: there is no contact beat, no follow-through and no
   * recovery. It is held for exactly as long as the sim holds the state, which
   * is the definition of sustained, and it hands the body back the same way a
   * gait change does.
   */
  private pose: string | null = null;
  /** The CLIP the running one-shot is playing. Internal — callers see commands. */
  private action: ActionName | null = null;
  /** What gameplay actually asked for, kept so the caller can be answered. */
  private actionCmd: AnimCommand | null = null;
  private actionElapsed = 0;
  private actionDuration = 0;
  /** Normalized ball-contact beat of the running action, or -1 for none. */
  private contactBeat = -1;
  /**
   * True while a one-shot owns the body. Read by setLocomotion, which must not
   * hand a gait any weight back until the action has released it. Stays true
   * through the hand-back ramp, or a gait change mid-ramp would crossfade a
   * second gait to weight 1 underneath it.
   */
  private suppressed = false;
  /**
   * `actionElapsed` at which the hand-back finishes and the action is torn down.
   * -1 while the action is still playing its own length. Replaces the old
   * "fade has been scheduled" boolean because an INTERRUPTED action ends at a
   * deadline that has nothing to do with its clip length.
   */
  private fadeDeadline = -1;
  /**
   * The one in-flight weight ramp. There is never more than one, and the object
   * is reused rather than reallocated — the render path allocates nothing
   * (§15.7) and twenty-two players striking a ball is not an excuse.
   */
  private readonly ramp: Ramp = {
    to: null, toStart: 0, from: [], fromStart: [], tearDown: [], elapsed: 0, dur: 0,
  };
  private rampActive = false;
  /** Guards one-shot gameplay events so an impulse can only fire once per play. */
  private eventFired = false;
  private missing: string[] = [];
  /**
   * His leg-length ratio against the master the clips were authored on. 1 for a
   * character built at the master's height, who then shares the pack untouched.
   * See hipsLegScale.ts.
   */
  readonly legScale: number;

  constructor(opts: AnimatorOptions) {
    this.mixer = new AnimationMixer(opts.root);
    // BEFORE the actions are built, because an action holds an interpolant over
    // the track it was given and re-binding one later is not a thing three
    // supports. Refitting the pack is the whole of the ground-contact fix.
    this.legScale = legScaleFromRestHips(opts.restHipsY);
    for (const [name, clip] of scaleClipsForLegs(opts.clips, this.legScale)) {
      const a = this.mixer.clipAction(clip);
      a.enabled = true;
      a.setEffectiveWeight(0);
      a.play();
      this.actions.set(name, a);
    }
    const idle = this.actions.get('Idle');
    if (idle) idle.setEffectiveWeight(1);
    else this.missing.push('Idle');
    // THE LADDER MAY ONLY OFFER STATES IT CAN ACTUALLY DRAW. `setLocomotion`
    // advances `this.loco` even when no action backs it (deliberately — see the
    // note there), so a band whose clip is absent would leave the player at
    // weight 0 in a bind pose. Choosing the table once, here, means a clip set
    // without the stroll degrades to exactly the ladder that shipped before it.
    this.bands = this.actions.has('SlowWalkForward')
      ? GAIT_BANDS_WITH_STROLL
      : GAIT_BANDS_NO_STROLL;
  }

  /** The gait ladder in force for THIS clip set. See the constructor. */
  private readonly bands: ReadonlyArray<GaitBand>;

  /** Clip names requested but absent from the GLB. Surfaced rather than ignored. */
  get missingClips(): readonly string[] {
    return this.missing;
  }

  /**
   * The clip that owns the BASE LAYER right now — a sustained pose if one is
   * held, otherwise the gait. Every weight decision about the base layer reads
   * this rather than `this.loco`, so there is exactly one answer to "what is
   * underneath the actions" and the pose and the gait cannot both claim it.
   */
  private get baseClip(): string {
    return this.pose ?? this.loco;
  }

  /** The sustained pose clip on the base layer, or null. Diagnostics/tests. */
  get poseClip(): string | null {
    return this.pose;
  }

  get locoState(): LocoState {
    return this.loco;
  }

  get activeAction(): ActionName | null {
    return this.action;
  }

  /** The SEMANTIC action running, if any. What gameplay should read. */
  get activeCommand(): AnimCommand | null {
    return this.actionCmd;
  }

  /** True while a one-shot owns the body (including its hand-back ramp). */
  get actionBusy(): boolean {
    return this.action !== null;
  }

  /**
   * Pick a gait from ground speed, applying hysteresis around the current one.
   * `lateral` and `reversing` override the forward ladder because a player
   * side-stepping at 2 m/s should not be running. `dribbling` is the same kind
   * of client-known refinement signal (derived from `ball.possessedBy`, never
   * a sim AnimState) and is checked after them for the same reason: a carrier
   * who is backpedaling or strafing with the ball at his feet still reads as
   * backpedaling or strafing, not dribbling forward.
   *
   * Gated to `Dribble`'s own honest speed range (NATURAL_SPEED.Dribble times
   * the rate clamp) rather than the whole forward ladder's span — outside it
   * the clip would have to stretch past RATE_MIN/RATE_MAX to match ground
   * speed, which is the moon-walk/sprint-cadence defect documented on RATE_MAX
   * above. A carrier walking the ball or sprinting with it still gets the
   * ordinary ladder.
   */
  selectLocoState(speed: number, reversing: boolean, lateral: number, dribbling: boolean): LocoState {
    if (reversing && speed > 0.35) return 'Backpedal';
    if (Math.abs(lateral) > 0.55 && speed > 0.35) {
      return lateral > 0 ? 'StrafeLeft' : 'StrafeRight';
    }
    if (dribbling && speed >= DRIBBLE_SPEED_MIN && speed <= DRIBBLE_SPEED_MAX) return 'Dribble';

    const idx = this.bands.findIndex((b) => b.state === this.loco);
    const cur = idx >= 0 ? idx : 0;
    let next = cur;
    // Step at most one band per evaluation so a spike cannot skip Walk→Sprint.
    if (cur + 1 < this.bands.length && speed >= this.bands[cur + 1]!.up) next = cur + 1;
    else if (cur > 0 && speed < this.bands[cur]!.down) next = cur - 1;
    // A gait we were not previously in (strafe/backpedal) has no band index, so
    // fall back to an absolute lookup.
    if (idx < 0) {
      next = 0;
      for (let i = this.bands.length - 1; i > 0; i--) {
        if (speed >= this.bands[i]!.up) {
          next = i;
          break;
        }
      }
    }
    return this.bands[next]!.state;
  }

  /**
   * Playback rate that makes the clip's stride match real ground speed.
   *
   * ⚠ THE DIVISOR IS PER-CLIP AND THE STRIDE IS PER-MAN, WHICH IS THE WHOLE
   * REASON FOR THE SCALE HERE. `NATURAL_SPEED` is one number per clip, measured
   * once on the master, and it has no per-character term. But a stride is bone
   * length times joint rotation: the clip supplies the rotations and the MAN
   * supplies the bones, so a 1.95 m keeper sweeps more ground per cycle than a
   * 1.68 m winger playing the identical clip at the identical rate. Whatever
   * that difference is goes into the feet as slide.
   *
   * The cast now spans 1.68-1.95 m across two clubs — about 16 % of leg length —
   * so on a walk that already skates 0.15 of its stride this is not a rounding
   * error. Scaling the divisor by his leg ratio makes a longer-legged man play
   * the cycle proportionally slower, so his stance sweeps the ground at the
   * speed the simulation says he is moving.
   *
   * Same ratio, same reasoning and the same source as the ground-contact fix in
   * hipsLegScale.ts: rest hip height over the master's. A character built at the
   * master's height has ratio 1 and this line changes nothing for him, which is
   * why it is safe to apply unconditionally.
   *
   * ⚠ IT MULTIPLIES `natural`, NOT THE RATE. Dividing the rate instead inverts
   * the correction — the tall man would play FASTER and slide further, in the
   * direction that looks like the bug getting worse.
   */
  playbackRateFor(state: LocoState, speed: number): number {
    const natural = NATURAL_SPEED[state];
    if (!natural || speed <= 0.01) return 1;
    return Math.min(RATE_MAX, Math.max(RATE_MIN, speed / (natural * this.legScale)));
  }

  private fadeSeconds(from: string, to: string): number {
    return FADE[`${from}>${to}`] ?? FADE.default!;
  }

  /**
   * Drive locomotion. Called every frame.
   * @param speed    horizontal ground speed, m/s
   * @param reversing moving opposite to facing
   * @param lateral  sideways component, +left
   * @param dribbling this slot currently has the ball at its feet (derived off
   *   `ball.possessedBy`, not a sim AnimState — see `selectLocoState`).
   *   Defaulted off so every existing caller (tests included) keeps working.
   */
  setLocomotion(speed: number, reversing: boolean, lateral: number, dribbling = false): void {
    let want = this.selectLocoState(speed, reversing, lateral, dribbling);

    // ⚠ A BAND CROSSED IN A BLINK MUST NOT COST A CROSS-FADE.
    //
    // The bands have hysteresis in SPEED but none in TIME, and a player
    // accelerating from a standstill crosses two of them faster than a fade can
    // finish. Measured in play, one start-and-stop:
    //
    //   Idle 32 ms -> Walk 124 ms -> Jog 216 ms -> Run 3831 ms
    //                             -> Jog 173 ms -> Walk 78 ms -> Idle
    //
    // against fades of 180-220 ms for those pairs. Every intermediate state was
    // held for LESS TIME THAN ITS OWN FADE, so each new cross-fade began on top
    // of one still in flight and three clips pulled the legs at once. That is
    // the jitter on every start and stop.
    //
    // The state itself must still change - `selectLocoState` steps one band per
    // evaluation ON PURPOSE, so a spike cannot skip Walk->Sprint, and blocking
    // that would break the ladder. What must not happen is a SECOND fade
    // starting while the first is still running. So when the previous change is
    // younger than its own fade, the gait is swapped OUTRIGHT instead of
    // cross-faded: one clip replaces another with no blend at all.
    //
    // That is only acceptable because the gaits are PHASE-ALIGNED (every one
    // starts on right-foot strike, within 0.045 of a cycle - see
    // tools/anim/measure/roll-clip.mjs). Two clips at the same point in the
    // stride can be swapped on a frame and the legs stay where they were. On the
    // unaligned pack this would have been a visible snap, which is why the fades
    // were doing the work before.
    const heldFor = this.clock - this.locoChangedAt;
    const stacking =
      want !== this.loco && heldFor < this.fadeSeconds(this.locoPrev, this.loco);

    const rate = this.playbackRateFor(want, speed);

    const cur = this.actions.get(this.loco);
    const nxt = this.actions.get(want);
    if (nxt) nxt.setEffectiveTimeScale(rate);

    if (want !== this.loco) {
      if (nxt) {
        if (this.suppressed || this.pose !== null) {
          // SOMETHING ELSE OWNS THE BODY: THE GAIT MAY CHANGE, BUT NOT ITS
          // WEIGHT. Either a one-shot (`suppressed`) or a sustained pose — the
          // two are different layers but the rule for the gait underneath them
          // is identical, and writing it once is what stops them disagreeing.
          //
          // crossFadeFrom raises the incoming gait to weight 1, which put it
          // straight back into the blend the suppression had just taken it out
          // of — so an action that started cleanly dropped to half strength the
          // moment the player's speed crossed a band edge underneath it. A pass
          // played while decelerating out of a sprint is not a rare case.
          //
          // The state machine still advances and the clip still PLAYS (phase is
          // preserved); only the weight is withheld, and the restore hands it to
          // whichever state the machine ended up in.
          nxt.enabled = true;
          nxt.setEffectiveWeight(0);
        } else if (cur && cur !== nxt) {
          // ⚠ CARRY THE STRIDE PHASE ACROSS. The swap below (and the fade in
          // the other branch) is only safe because the gaits are PHASE-ALIGNED
          // — every one starts on right-foot strike. That makes two clips
          // interchangeable AT THE SAME POINT IN THE STRIDE, and nothing was
          // putting them at the same point: the incoming action resumed at
          // whatever playhead it was left at the last time that gait was used.
          //
          // Measured, ladder frame 477 -> 478, reproduced at the same frame in
          // all four camera runs:
          //     477   Jog:0.667 @ 0.0141   (phase 0.014 of its cycle)
          //     478   Walk:1    @ 0.4225   (phase 0.364 of its cycle)
          // Right-foot-strike was replaced by mid-stride on one frame and the
          // left foot moved 0.77 m. That is the pop on every deceleration.
          syncPhase(cur, nxt);
          nxt.enabled = true;
          nxt.setEffectiveWeight(1);
          if (stacking) {
            // hard swap — see the note above `stacking`
            cur.setEffectiveWeight(0);
            cur.enabled = false;
            nxt.enabled = true;
            nxt.setEffectiveWeight(1);
          } else {
            nxt.crossFadeFrom(cur, this.fadeSeconds(this.loco, want), false);
          }
        } else {
          nxt.enabled = true;
          nxt.setEffectiveWeight(1);
        }
      } else if (!this.missing.includes(want)) {
        this.missing.push(want);
      }
      // The logical state advances whether or not a clip backs it. Gating this
      // on the action existing meant one absent clip froze the state machine on
      // Idle for the rest of the session - the player would keep sliding around
      // in an idle pose and nothing would say why. Tracking the state and
      // recording the gap in missingClips makes the fault visible instead.
      this.locoPrev = this.loco;
      this.locoChangedAt = this.clock;
      this.loco = want;
    }
  }

  /**
   * A one-shot OWNS the body while it runs, so the locomotion underneath it has
   * to get out of the way.
   *
   * It did not. `playAction` set the action to weight 1 and left the gait
   * action at weight 1 too, and three averages two equally-weighted actions on
   * the same tracks 50/50 — so a Pass played as half a pass over half an idle,
   * a ThrowIn never got its arms overhead, and every action in the set read as
   * "barely moves the rig". The clips were fine the whole time.
   *
   * Suppression is by weight, not by stopping: the gait action keeps advancing
   * so it resumes in phase rather than snapping back to frame 0 afterwards.
   *
   * RELEASING IS INSTANT and that is deliberate. The gait was already carried
   * from 0 to 1 by the hand-back ramp (`beginHandback`), so this only settles
   * the final integer. Handing it back with a SECOND, independent `fadeIn`
   * would restart it from 0 against an action already at 0 — the whole window
   * would sum to less than 1 and three lerps every shortfall toward the BIND
   * pose, which is the sag after every pass this wiring exists to prevent.
   */
  private suppressLocomotion(on: boolean): void {
    this.suppressed = on;
    for (const [n, a] of this.actions) {
      if (ONE_SHOTS.includes(n)) continue;
      if (!a.enabled) continue;
      // `baseClip`, not `loco`: when a sustained pose holds the base layer the
      // gait must stay at zero on release, or the action would hand the body
      // back to a jog underneath a keeper who is still in his set stance.
      const w = on ? 0 : n === this.baseClip ? 1 : 0;
      // setEffectiveWeight also clears any in-flight fade, so the gait's weight
      // is exactly what is asked for here and not whatever a crossfade left.
      a.setEffectiveWeight(w);
    }
  }

  /**
   * TAKE OR RELEASE THE BASE LAYER WITH A SUSTAINED POSE.
   *
   * @param clip the pose clip to hold, or null to hand the base layer back to
   *   the gait ladder.
   *
   * Routed through the SAME `startRamp` the one-shots use, and that is the whole
   * reason it is safe: the ramp captures every currently-weighted action and
   * scales the outgoing set against the incoming one, so the §16 invariant
   * (total ≈ 1, no shortfall toward the bind pose) holds here by construction
   * rather than by a second implementation that has to be kept in agreement.
   *
   * Under a running one-shot the swap is INSTANT and weightless. The base layer
   * is already held at 0 by `suppressLocomotion`, so there is no weight to
   * cross-fade; ramping anyway would fight the action's own ramp for the one
   * `Ramp` slot and strand whatever it had captured.
   */
  setSustainedPose(clip: string | null): void {
    if (clip === this.pose) return;
    const from = this.baseClip;
    const next = clip === null ? this.loco : clip;
    const a = clip === null ? this.actions.get(this.loco) : this.actions.get(clip);
    if (clip !== null && !a) {
      // Bound to a clip the loaded set does not contain. Report it and stay on
      // the gait rather than freezing the body on nothing.
      if (!this.missing.includes(clip)) this.missing.push(clip);
      return;
    }
    this.pose = clip;
    if (!a) return;

    a.enabled = true;
    // Sustained, so it LOOPS and never clamps — the opposite of a one-shot. A
    // pose left on LoopOnce from a previous life would freeze on its last frame
    // and stop being a held stance.
    a.setLoop(LoopRepeat, Infinity);
    a.clampWhenFinished = false;
    a.setEffectiveTimeScale(1);
    a.play();

    if (this.suppressed) {
      // The action owns the body: swap which clip is the base underneath it.
      //
      // ⚠ THE OUTGOING BASE'S WEIGHT IS TRANSFERRED, NOT DISCARDED, and that is
      // the whole correctness of this branch. `suppressed` means a one-shot has
      // TAKEN the body, not that it has finished taking it: during
      // ACTION_IN_FADE the action is at (say) 0.83 and the gait still legitimately
      // holds the other 0.17. Zeroing the gait outright at that instant dropped
      // the total to 0.83, and three lerps every shortfall below 1 toward the
      // BIND POSE — a player a sixth of the way into a T-pose on the frame he
      // hits the floor.
      //
      // It was unreachable while every sustained non-gait command was
      // NOT_YET_AVAILABLE (setLocomotionCommand took the `null` path and never
      // got here). Binding FALL and STUMBLE to real clips made it reachable on
      // the single most common route into it: a knockdown interrupting a swing,
      // which is exactly when `overridesAction` fires.
      const prev = this.actions.get(from);
      const carried = prev && prev !== a && prev.enabled ? prev.getEffectiveWeight() : 0;
      a.setEffectiveWeight(carried);
      if (prev && prev !== a) prev.setEffectiveWeight(0);
      // If that outgoing clip was part of an in-flight ramp, the ramp is still
      // scaling IT down by (1−t). Substitute the new pose in its place so the
      // ramp keeps operating on the action that actually holds the weight;
      // otherwise the ramp drives a zeroed action and the sum drifts.
      if (this.rampActive && prev && prev !== a) {
        const i = this.ramp.from.indexOf(prev);
        if (i >= 0) this.ramp.from[i] = a;
      }
      return;
    }
    if (next === from) return;
    this.startRamp(a, POSE_FADE);
  }

  /**
   * Drive the one in-flight weight ramp by hand, BEFORE the mixer runs.
   *
   * THE INVARIANT, as algebra rather than as hope. The whole outgoing set is
   * scaled by (1−t) while the incoming one is lerped to 1, so the total is
   *     (1−t)·Σ fromStart + toStart + (1−toStart)·t
   *   = (1−t)·(1−toStart) + toStart + (1−toStart)·t
   *   = 1
   * for EVERY t, whenever the blend started summing to 1 — and the controller
   * never lets it start any other way. This matters because three lerps any
   * shortfall below 1 toward the BIND POSE: a sum of 0.83 is a player a sixth of
   * the way into a T-pose, on the exact frame he strikes the ball.
   *
   * Keeping the outgoing side ENABLED (rather than `fadeOut`ing it) is what
   * preserves the gait phase: a disabled action stops advancing its playhead.
   *
   * If the gait changes mid-ramp the new one stays suppressed at 0 and this
   * keeps ramping the captured ones down, so the sum holds either way.
   */
  private stepRamp(dt: number): void {
    if (!this.rampActive) return;
    const r = this.ramp;
    const t = r.dur > 0 ? Math.min(1, r.elapsed / r.dur) : 1;
    r.to?.setEffectiveWeight(r.toStart + (1 - r.toStart) * t);
    for (let i = 0; i < r.from.length; i++) {
      r.from[i]!.setEffectiveWeight(r.fromStart[i]! * (1 - t));
    }
    r.elapsed += dt;
    if (t >= 1) this.settleRamp();
  }

  /** Finish the in-flight ramp NOW, at its end state. */
  private settleRamp(): void {
    if (!this.rampActive) return;
    this.rampActive = false;
    const r = this.ramp;
    r.to?.setEffectiveWeight(1);
    for (const a of r.from) a.setEffectiveWeight(0);
    // A superseded one-shot left merely at weight 0 but still enabled keeps its
    // clamped final pose scheduled on the mixer, quietly biasing later blends.
    for (const a of r.tearDown) this.teardown(a);
    r.to = null;
    r.from.length = 0;
    r.fromStart.length = 0;
    r.tearDown.length = 0;
  }

  /**
   * Arm a ramp onto `to`, carrying EVERY currently-weighted action out of it.
   *
   * Capturing the live weights (rather than assuming a gait sits at 1) is what
   * makes an interruption safe: a request that lands halfway through another
   * ramp finds a body split between three clips and takes all three down
   * together.
   */
  private startRamp(to: AnimationAction, dur: number): void {
    const r = this.ramp;
    r.from.length = 0;
    r.fromStart.length = 0;
    r.tearDown.length = 0;
    let outgoing = 0;
    for (const [n, a] of this.actions) {
      if (a === to || !a.enabled) continue;
      const w = a.getEffectiveWeight();
      if (w <= 0) continue;
      r.from.push(a);
      r.fromStart.push(w);
      outgoing += w;
      if (ONE_SHOTS.includes(n)) r.tearDown.push(a);
    }
    if (outgoing <= 0) {
      // Nothing held the body (every clip for this state is missing), so there
      // is no sum to preserve and the incoming clip simply takes it outright.
      this.rampActive = false;
      to.setEffectiveWeight(1);
      return;
    }
    r.to = to;
    r.toStart = to.getEffectiveWeight();
    r.elapsed = 0;
    r.dur = dur;
    this.rampActive = true;
  }

  /** Full teardown of a one-shot: no weight, no schedule, no residual pose. */
  private teardown(a: AnimationAction): void {
    a.stop();
    // reset() re-ENABLES the action, so disabling has to come after it.
    a.reset();
    a.enabled = false;
    a.setEffectiveWeight(0);
  }

  /**
   * Can this command actually be drawn — and if not, exactly why not?
   *
   * The lifecycle's validation step. A caller that asks for a goalkeeper dive
   * gets `NotYetAvailable`, not a lie and not an exception: another agent is
   * authoring those clips, the gap is known, and the player must keep running
   * rather than freeze or T-pose while it is filled.
   */
  availabilityOf(cmd: AnimCommand): ActionAvailability {
    if (!isOneShotCommand(cmd)) return ActionAvailability.NotAnAction;
    const binding = CLIP_FOR_COMMAND[cmd];
    if (binding === NOT_YET_AVAILABLE) return ActionAvailability.NotYetAvailable;
    return this.actions.has(binding) ? ActionAvailability.Available : ActionAvailability.ClipMissing;
  }

  /** Say once, per command, per session, that something cannot be drawn. */
  private noteUnavailable(cmd: AnimCommand, why: ActionAvailability): void {
    if (why === ActionAvailability.ClipMissing) {
      const binding = CLIP_FOR_COMMAND[cmd];
      if (typeof binding === 'string' && !this.missing.includes(binding)) {
        this.missing.push(binding);
      }
    }
    if (unavailableLogged.has(cmd)) return;
    unavailableLogged.add(cmd);
    const reason =
      why === ActionAvailability.NotYetAvailable
        ? 'NOT_YET_AVAILABLE (no clip authored yet)'
        : 'bound to a clip that is missing from the loaded set';
    console.info(
      `[anim] ${animCommandName(cmd)} is ${reason} — holding locomotion instead. ` +
        `This is a known gap, not a fallback: see CLIP_FOR_COMMAND in playerAnimator.ts.`,
    );
  }

  /**
   * GAMEPLAY ASKS FOR A ONE-SHOT. The single entry point, and it speaks
   * semantics — there is no overload that takes a clip name.
   *
   * Refuses while another action owns the body unless `interrupt` is set. The
   * simulation is authoritative for discrete actions, so when the SIM changes
   * its mind mid-action the renderer must follow it (interrupt); a duplicate
   * request for the action already running must not restart it (refuse).
   *
   * @returns true only if the action actually started.
   */
  requestAction(cmd: AnimCommand, opts?: { interrupt?: boolean }): boolean {
    const avail = this.availabilityOf(cmd);
    if (avail === ActionAvailability.NotAnAction) return false;
    if (avail !== ActionAvailability.Available) {
      this.noteUnavailable(cmd, avail);
      return false;
    }
    if (this.action && !opts?.interrupt) return false;
    return this.beginAction(cmd, CLIP_FOR_COMMAND[cmd] as string);
  }

  /** Start the clip, take the body over on a ramp that never dips below 1. */
  private beginAction(cmd: AnimCommand, clip: string): boolean {
    const a = this.actions.get(clip);
    if (!a) {
      if (!this.missing.includes(clip)) this.missing.push(clip);
      return false;
    }
    // THE WEIGHT THIS ACTION ALREADY HOLDS IS CARRIED INTO THE RAMP, NOT ZEROED.
    //
    // Sampled BEFORE `reset()`, and it is the whole correctness of a re-arm.
    // When the sim accepts the SAME action again (§11 — a pass played straight
    // back, `anim` written Pass over Pass with a new execution id), `a` is the
    // action already on screen at, say, 0.75 while the gait holds 0.25. Zeroing
    // it here threw that 0.75 away: `startRamp` excludes `to` from the outgoing
    // set, so it captured toStart = 0 against an outgoing sum of 0.25 and the
    // body totalled 0.25 on the take-over frame — three lerps the missing 0.75
    // toward the BIND POSE, which is a player three-quarters into a T-pose on
    // the exact frame he strikes the ball. Measured, not theorised: the §11
    // re-arm test reported 0.2500 the moment it existed.
    //
    // Carrying it is also what the ramp's algebra already assumes — Σ fromStart
    // = 1 − toStart holds precisely because `to`'s own weight was never
    // discarded. `reset()` restarts the PLAYHEAD (the second swing must begin at
    // frame 0) and leaves weight alone, so the two concerns stay separate.
    //
    // `enabled` gates the read: a stale disabled action can hold an arbitrary
    // leftover weight that is not on the body, and adopting it would inflate the
    // sum instead of preserving it.
    const carried = a.enabled ? a.getEffectiveWeight() : 0;
    a.reset();
    a.enabled = true;
    a.setLoop(LoopOnce, 1);
    a.clampWhenFinished = true;
    a.setEffectiveTimeScale(1);
    a.play();
    a.setEffectiveWeight(carried);

    this.action = clip as ActionName;
    this.actionCmd = cmd;
    this.actionElapsed = 0;
    this.actionDuration = a.getClip().duration;
    this.contactBeat = ACTION_CONTACT_BEAT[cmd];
    this.eventFired = false;
    this.fadeDeadline = -1;

    // The gait keeps ADVANCING underneath (phase preserved — the ramp only
    // touches weights and never disables), so locomotion resumes mid-stride.
    // `suppressed` is raised BEFORE the capture so a gait change during the
    // action cannot crossfade a second gait back up to weight 1 underneath it.
    this.suppressed = true;
    this.startRamp(a, ACTION_IN_FADE);
    return true;
  }

  /**
   * INTERRUPT the running action and hand the body back to locomotion.
   *
   * The safe interruption the lifecycle requires: it rides the same ramp the
   * natural ending uses, so a tackle that is cut short by a foul does not pop.
   * Idempotent — calling it during the hand-back changes nothing.
   */
  stopAction(fade = ACTION_FADE): void {
    if (!this.action || this.fadeDeadline >= 0) return;
    this.beginHandback(fade);
  }

  /**
   * HARD reset: the body is no longer this player's.
   *
   * Used when a slot is recycled onto a different footballer — there is nobody
   * to ramp back to, and a leftover one-shot weight would bias the FIRST frame
   * the new player is drawn on. Leaves zero residual by construction.
   */
  resetAction(): void {
    this.rampActive = false;
    this.ramp.to = null;
    this.ramp.from.length = 0;
    this.ramp.fromStart.length = 0;
    this.ramp.tearDown.length = 0;
    if (this.action) {
      const a = this.actions.get(this.action);
      if (a) this.teardown(a);
    }
    this.action = null;
    this.actionCmd = null;
    this.actionElapsed = 0;
    this.actionDuration = 0;
    this.contactBeat = -1;
    this.eventFired = false;
    this.fadeDeadline = -1;
    this.suppressLocomotion(false);
  }

  /** Begin the action→gait ramp and schedule the teardown at its end. */
  private beginHandback(fade: number): void {
    if (!this.action) return;
    this.fadeDeadline = this.actionElapsed + fade;
    const gait = this.actions.get(this.loco);
    if (!gait) return;
    gait.enabled = true;
    this.startRamp(gait, fade);
  }

  /** The action is over: tear it down and give locomotion the body outright. */
  private finishAction(): void {
    this.settleRamp();
    if (this.action) {
      const a = this.actions.get(this.action);
      if (a) this.teardown(a);
    }
    this.action = null;
    this.actionCmd = null;
    this.contactBeat = -1;
    this.fadeDeadline = -1;
    this.eventFired = false;
    // Instant, not a fade: the ramp above already carried the gait to 1.
    this.suppressLocomotion(false);
  }

  /**
   * DRIVE LOCOMOTION FROM THE SIMULATION'S OWN STATE, refined by velocity.
   *
   * The authority split, in one method:
   *   - DIRECTION (backpedal / strafe left / strafe right) is the SIM's. It
   *     compares velocity against body yaw with its own hysteresis thresholds
   *     (BACKPEDAL_ANGLE_COS / STRAFE_ANGLE_COS in player-controller.ts).
   *     Re-deriving that from interpolated render positions is precisely the
   *     duplicate state machine this wiring deletes — and it is what made
   *     backward travel play as a forward cycle in reverse.
   *   - WHICH FORWARD GAIT and the PLAYBACK RATE are velocity's, because a
   *     render frame and a sim tick disagree about the exact moment a player
   *     crosses 2.85 m/s and honouring the tick edge makes the gait flicker.
   *     `selectLocoState`'s hysteresis is the reason this is safe.
   *
   * A one-shot command still drives the gait underneath it: the suppression
   * holds its WEIGHT at zero but its phase keeps advancing, so locomotion
   * resumes mid-stride when the action hands the body back.
   */
  setLocomotionCommand(
    cmd: AnimCommand, speed: number, reversing: boolean, lateral: number, dribbling = false,
  ): void {
    if (isOneShotCommand(cmd)) {
      // A one-shot does not change the base layer's IDENTITY — the pose (if any)
      // stays held underneath and is what the action hands the body back to.
      this.setLocomotion(speed, reversing, lateral, dribbling);
      return;
    }
    // A SUSTAINED STATE THAT IS NOT A GAIT holds its own clip on the base layer
    // (jockey, fallen, stumble, throw-in hold, keeper set). Cleared on any gait
    // command, so leaving the state releases the pose in the same call that
    // picks the gait to release it to.
    const pose = sustainedPoseClip(cmd);
    if (pose !== null) {
      this.setSustainedPose(pose);
      // The gait keeps tracking underneath at weight 0, exactly as it does under
      // a one-shot, so leaving the pose resumes a stride rather than frame 0.
      this.setLocomotion(speed, reversing, lateral, dribbling);
      return;
    }
    this.setSustainedPose(null);
    if (CLIP_FOR_COMMAND[cmd] === NOT_YET_AVAILABLE) {
      // A sustained state nobody has drawn yet (jockey, fallen, keeper stance).
      // Say so once and keep the legs moving — a frozen or T-posed player is a
      // worse answer than the wrong gait, and the console line is what stops
      // this being mistaken for a controller bug when the clip lands.
      this.noteUnavailable(cmd, ActionAvailability.NotYetAvailable);
      this.setLocomotion(speed, reversing, lateral, dribbling);
      return;
    }
    if (isDirectionalGait(cmd)) {
      this.setLocomotion(
        speed,
        cmd === AnimCommand.BACKPEDAL,
        cmd === AnimCommand.STRAFE_LEFT ? 1 : cmd === AnimCommand.STRAFE_RIGHT ? -1 : 0,
        dribbling,
      );
      return;
    }
    this.setLocomotion(speed, false, 0, dribbling);
  }

  /**
   * BALL CONTACT — true exactly once per action play, on the first frame at or
   * past `normalizedTime`. The token is what stops a ball impulse firing every
   * frame while the contact frame is on screen.
   *
   * Called with NO argument it uses the running command's own beat from
   * `ACTION_CONTACT_BEAT`, which is read from the simulation's `ACTION_DEFS`
   * rather than copied — so the visible contact and the sim's impulse are
   * timed off one table, not two. Actions that never touch the ball (a get-up,
   * a celebration) carry a beat of -1 and raise nothing at all, instead of
   * raising a contact at time zero.
   */
  consumeActionEvent(normalizedTime: number = this.contactBeat): boolean {
    if (!this.action || this.eventFired || this.actionDuration <= 0) return false;
    if (normalizedTime < 0) return false;
    if (this.actionElapsed / this.actionDuration >= normalizedTime) {
      this.eventFired = true;
      return true;
    }
    return false;
  }

  /** Locomotion clip weights, for the debug overlay. */
  weights(): Record<string, number> {
    const out: Record<string, number> = {};
    for (const [n, a] of this.actions) {
      const w = a.getEffectiveWeight();
      if (w > 0.0001) out[n] = Number(w.toFixed(3));
    }
    return out;
  }

  /**
   * Read-only snapshot of one clip's action, or null when the clip is absent.
   *
   * The only window onto the blend that reports zero weights, playheads and
   * teardown state. Nothing may be mutated through it.
   */
  inspect(clip: string): ActionSnapshot | null {
    const a = this.actions.get(clip);
    if (!a) return null;
    return {
      weight: a.getEffectiveWeight(),
      time: a.time,
      enabled: a.enabled,
      running: a.isRunning(),
    };
  }

  /** Sum of weights held by one-shot actions. Should be ~0 during locomotion. */
  actionResidual(): number {
    let sum = 0;
    for (const n of ONE_SHOTS) {
      const a = this.actions.get(n);
      if (a) sum += a.getEffectiveWeight();
    }
    return sum;
  }

  /**
   * THE LAST LINE OF DEFENCE ON §16: the weights on the body must sum to 1.
   *
   * three lerps ANY shortfall below 1 toward the BIND POSE, so a total of 0.42
   * is a player 58 % of the way into a T-pose — not a subtle blend error, a
   * visibly broken frame.
   *
   * ⚠ FOUND IN A DRIVEN CAPTURE, and the path is one no amount of care in the
   * transition code was going to cover. At a band edge the gait FLIP-FLOPPED
   * SlowWalkForward -> StrafeRight -> SlowWalkForward inside four frames
   * (`actions_running`, 9.30-9.40 s, identical in both camera runs):
   *
   *     9.300  StrafeRight:1.000  SlowWalkForward:0.917   sum 1.92
   *     9.350  StrafeRight:1.000  SlowWalkForward:0.667   sum 1.67
   *     9.367  StrafeRight gone   SlowWalkForward:0.583   sum 0.58  <-- bleed
   *     9.400                     SlowWalkForward:0.417   sum 0.42
   *
   * Going back to a gait it was already ON means `want === this.loco`, so the
   * transition code does nothing at all — while that same clip is still being
   * ramped DOWN by the fade that was taking it away. Nothing owned putting it
   * back, and the shortfall went straight to the bind pose.
   *
   * Rather than teach every transition path about every other one, the
   * invariant is enforced where it is finally true or false: immediately before
   * the mixer reads the weights. Only ever ADDS, only to the gait that is
   * supposed to own the body, and only when the sum is genuinely short — a
   * healthy crossfade sums to 1 and is never touched. Suppressed and posed
   * frames are skipped, because there the action or the pose legitimately owns
   * the total.
   */
  private holdTheSum(): void {
    if (this.suppressed || this.pose !== null) return;
    let sum = 0;
    for (const [, a] of this.actions) if (a.enabled) sum += a.getEffectiveWeight();
    if (sum >= 0.999 || sum <= 0) return;
    const cur = this.actions.get(this.loco);
    if (!cur) return;
    cur.enabled = true;
    cur.setEffectiveWeight(Math.min(1, cur.getEffectiveWeight() + (1 - sum)));
  }

  update(dt: number): void {
    this.clock += dt;
    // BEFORE the mixer, or the weights this sets are a frame late — which is
    // exactly the frame the action starts on.
    this.stepRamp(dt);
    this.holdTheSum();
    this.mixer.update(dt);

    if (this.action) {
      this.actionElapsed += dt;
      // FOLLOW-THROUGH ENDS → hand the body back on a single stepped ramp, so
      // action-out and gait-in sum to exactly 1 across the whole window. The
      // previous shape called three's `fadeOut` once per frame, which
      // reschedules from FULL weight every time: the action sat at ~0.9 for the
      // entire window and was then hard-cut to 0 at teardown — a pop wearing a
      // fade's name.
      if (this.fadeDeadline < 0 && this.actionElapsed >= this.actionDuration) {
        this.beginHandback(ACTION_FADE);
      }
      // RECOVERY COMPLETE → full teardown, not just a fade. A clamped LoopOnce
      // action left enabled keeps contributing its final pose at a small weight
      // and quietly biases every later blend.
      if (this.fadeDeadline >= 0 && this.actionElapsed >= this.fadeDeadline) {
        this.finishAction();
      }
    }

    // Sweep any action that has decayed below audibility but is still enabled.
    for (const n of ONE_SHOTS) {
      const a = this.actions.get(n);
      if (!a || n === this.action) continue;
      if (a.enabled && a.getEffectiveWeight() < RESIDUAL_EPSILON) {
        a.stop();
        a.enabled = false;
        a.setEffectiveWeight(0);
      }
    }
  }

  dispose(): void {
    this.mixer.stopAllAction();
    this.actions.clear();
  }
}
